import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Shield } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="font-mono-display text-xl text-white mb-3">{title}</h2>
    <div className="text-sm text-zinc-300/90 leading-relaxed space-y-3">{children}</div>
  </section>
);

const LAST_UPDATED = 'May 17, 2026';

export default function PrivacyPolicy() {
  const { isAuthed } = useAuth();

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-10 sm:py-12">
        <div className="mb-8">
          <Link to="/" className="btn-ghost inline-flex mb-6">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 text-xs uppercase tracking-widest mb-4">
            <Lock className="w-3.5 h-3.5" />
            Legal // privacy
          </div>
          <h1 className="font-mono-display text-3xl sm:text-4xl text-white mb-3">
            Privacy <span className="neon-cyan">Policy</span>
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base max-w-2xl leading-relaxed">
            This policy explains what information ValoRex collects, how we use it, and the choices you have when you use our platform.
          </p>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mt-4">
            Last updated: <span className="text-cyan-300/80">{LAST_UPDATED}</span>
          </p>
        </div>

        <div className="neon-card rounded-lg p-6 sm:p-8 mb-6 flex items-start gap-4 border-cyan-400/20">
          <div className="w-10 h-10 shrink-0 grid place-items-center rounded border border-cyan-400/40 bg-cyan-400/10">
            <Shield className="w-5 h-5 text-cyan-300" />
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            We treat account linking credentials and profile data seriously. We do{' '}
            <span className="text-white">not sell your personal information</span> and we only share data as described in this policy or when required by law.
          </p>
        </div>

        <div className="neon-card rounded-lg p-6 sm:p-10 bracket-corners space-y-10">
          <Section id="intro" title="1. Who we are">
            <p>
              ValoRex (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates valorex.space and related applications (the &quot;Service&quot;).
              This Privacy Policy applies when you register, link Valorant accounts, use the dashboard, submit billing information, or view public share pages.
            </p>
          </Section>

          <Section id="collect" title="2. Information we collect">
            <p>Depending on how you use the Service, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-zinc-400">
              <li>
                <span className="text-zinc-300">Account data:</span> username, email address, subscription plan, and slot usage
              </li>
              <li>
                <span className="text-zinc-300">Linked game data:</span> Riot account identifiers, IGN, tag, region, inventory summaries, ranks, store
                snapshots, match history metadata, and other data returned through Access URL / API flows
              </li>
              <li>
                <span className="text-zinc-300">Billing data:</span> payment method labels, amounts, transaction references, and proof you submit for manual verification
              </li>
              <li>
                <span className="text-zinc-300">Technical data:</span> IP address, browser type, device information, and server logs used for security and debugging
              </li>
              <li>
                <span className="text-zinc-300">Communications:</span> messages you send to support or admin channels, if applicable
              </li>
            </ul>
          </Section>

          <Section id="use" title="3. How we use your information">
            <p>We use collected information to:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-zinc-400">
              <li>Provide, maintain, and improve the Service</li>
              <li>Authenticate you and manage your vault, cooldowns, and plan limits</li>
              <li>Sync and display Valorant account data you authorize</li>
              <li>Process manual subscription payments and admin verification</li>
              <li>Generate public share pages when you choose to share a link</li>
              <li>Detect abuse, fraud, and security incidents</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </Section>

          <Section id="riot" title="4. Riot Games and third parties">
            <p>
              When you link an account, data is obtained from Riot Games systems subject to Riot&apos;s own privacy practices. We are not responsible
              for Riot&apos;s collection or use of your data outside ValoRex.
            </p>
            <p>
              We use infrastructure providers (such as hosting and database services) that process data on our behalf under contractual safeguards.
              We do not authorize them to use your data for their own marketing purposes.
            </p>
          </Section>

          <Section id="sharing" title="5. Public sharing and disclosure">
            <p>
              If you enable or copy a share link, the information configured for that public page may be visible to anyone with the URL. You are
              responsible for what you share.
            </p>
            <p>We may disclose information if we believe it is necessary to:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-zinc-400">
              <li>Comply with law, regulation, legal process, or governmental request</li>
              <li>Protect the rights, property, or safety of ValoRex, our users, or the public</li>
              <li>Investigate violations of our terms or suspected fraud</li>
            </ul>
          </Section>

          <Section id="storage" title="6. Cookies and local storage">
            <p>
              We use browser local storage to keep you signed in (for example, authentication tokens). Essential cookies or similar technologies may
              be used for session security. We do not use third-party advertising cookies on the core Service.
            </p>
            <p>You can clear site data in your browser, which will sign you out and remove locally stored tokens.</p>
          </Section>

          <Section id="retention" title="7. Data retention">
            <p>
              We retain your account and linked game data while your account is active or as needed to provide the Service. If you delete your account
              or request removal, we will delete or anonymize personal data within a reasonable period, except where we must retain records for legal,
              security, or billing purposes.
            </p>
          </Section>

          <Section id="security" title="8. Security">
            <p>
              We use administrative, technical, and organizational measures designed to protect your information, including encryption for sensitive
              credentials and access controls on production systems. No method of transmission or storage is completely secure; we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="rights" title="9. Your choices and rights">
            <p>You may:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-zinc-400">
              <li>Access and update profile information through your account settings where available</li>
              <li>Remove linked Valorant accounts from your dashboard</li>
              <li>Stop using public share links by not distributing them or removing shared content</li>
              <li>Request account deletion or data access by contacting us</li>
            </ul>
            <p>
              Depending on your location, you may have additional rights under applicable privacy laws (such as access, correction, deletion, or
              objection). Contact us to exercise those rights.
            </p>
          </Section>

          <Section id="children" title="10. Children">
            <p>
              The Service is not directed to children under 13 (or the minimum age in your jurisdiction). We do not knowingly collect personal
              information from children. If you believe a child has provided us data, contact us so we can delete it.
            </p>
          </Section>

          <Section id="international" title="11. International users">
            <p>
              ValoRex may be operated from jurisdictions outside your country. By using the Service, you understand that your information may be
              processed and stored in locations that may have different data protection laws than your own.
            </p>
          </Section>

          <Section id="changes" title="12. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date will reflect the latest version. Material changes
              may be communicated through the Service where appropriate. Continued use after updates means you accept the revised policy.
            </p>
          </Section>

          <Section id="contact" title="13. Contact us">
            <p>
              For privacy questions, data requests, or concerns, reach us through the contact channels published on the site or the support address
              used for ValoRex operations.
            </p>
            <p>
              See also our <Link to="/terms-of-service" className="text-cyan-300 hover:text-cyan-200">Terms of Service</Link> for rules governing use of the platform.
            </p>
          </Section>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={isAuthed ? '/dashboard' : '/auth/register'} className="btn-neon">
            {isAuthed ? 'Dashboard' : 'Get Started'}
          </Link>
          <Link to="/terms-of-service" className="btn-ghost">
            Terms of service
          </Link>
        </div>
      </section>
    </Layout>
  );
}
